import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import clsx from 'clsx';
import { TypedMemo } from '../../../../common/types';
import { formatPlanPrice, useDisplayCurrency } from '../../../config/useDisplayCurrency';
import { PlanHighlight } from '../../../plans/planHighlights';
import { PlanHighlightItems } from '../../../plans/PlanHighlightItems';
import { planHighlightMessages } from '../../../plans/PlanHighlightList';
import { scrollToPlansCompare } from '../../../plans/scrollToPlansCompare';
import { pricingCardMessages } from './pricingCardMessages';
import styles from './PricingFreeRow.module.scss';

const messages = defineMessages({
  forever: { id: 'app.sml.pricing.period.forever', defaultMessage: 'forever' },
  afterTrial: { id: 'app.sml.pricing.plan.free.note', defaultMessage: 'Your plan after the trial' },
  yourPlan: { id: 'app.sml.plans.free.noteCurrent', defaultMessage: 'Your plan' },
  always: { id: 'app.sml.plans.free.noteAlways', defaultMessage: 'Always there if you cancel' },
});

interface PricingFreeRowProps {
  name: string;
  description: string;
  highlights: PlanHighlight[];
  isCurrent: boolean;
  /** The workspace runs the reverse trial: Free is the plan it falls back to. */
  isTrialing: boolean;
  /** Inside a native app's web view: no web price. */
  hidePrice: boolean;
}

/**
 * Free as one full-width row above the paid plans (design plans-page a): the name and the price,
 * what Free gives in two columns, and a note on the right. Free has no button.
 */
const PricingFreeRowComponent: React.FC<PricingFreeRowProps> = ({
  name,
  description,
  highlights,
  isCurrent,
  isTrialing,
  hidePrice,
}) => {
  const intl = useIntl();
  const currency = useDisplayCurrency();
  const note = isTrialing ? messages.afterTrial : isCurrent ? messages.yourPlan : messages.always;

  return (
    <div className={clsx(styles.row, isCurrent && styles.current)} data-testid="pricing-free-row">
      <div className={styles.head}>
        <div className={styles.name}>
          <span>{name}</span>
          {isCurrent && <span className={styles.chip}>{intl.formatMessage(pricingCardMessages.currentPlan)}</span>}
        </div>
        <p className={styles.desc}>{description}</p>
        {!hidePrice && (
          <div className={styles.price}>
            <span className={styles.amt}>{formatPlanPrice(intl, 0, currency)}</span>
            <span className={styles.per}>{intl.formatMessage(messages.forever)}</span>
          </div>
        )}
      </div>
      <PlanHighlightItems twoColumns highlights={highlights} />
      <div className={styles.side}>
        <div className={styles.note}>{intl.formatMessage(note)}</div>
        <button type="button" className={styles.more} onClick={scrollToPlansCompare}>
          {intl.formatMessage(planHighlightMessages.seeAll)}
        </button>
      </div>
    </div>
  );
};

export const PricingFreeRow = TypedMemo(PricingFreeRowComponent);
